import { FC, useState } from "react";
import { Building2, ChevronDown, ChevronUp, X } from "lucide-react";
import { CoproRegistryEntryDto } from "api/referential";

interface CopropertyRegistryCardProps {
  entry: CoproRegistryEntryDto;
  /** Désélection depuis la fiche (le clic sur la ligne sélectionnée fait de même). */
  onClear?: () => void;
}

/** Une ligne libellé/valeur : format compact qui tient dans le panneau latéral étroit. */
const Info: FC<{ label: string; value?: string | number | null }> = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-3 py-1.5">
    <dt className="shrink-0 text-xs text-gray-500">{label}</dt>
    <dd className="min-w-0 break-words text-right text-sm font-medium text-gray-900">
      {value ?? "—"}
    </dd>
  </div>
);

/** « 72 (60 habitation) » — les lots sont le premier repère de taille d'une copro. */
const formatLots = (entry: CoproRegistryEntryDto): string | null => {
  if (entry.nombreTotalLot == null) return null;
  return entry.nombreLotHabitation != null
    ? `${entry.nombreTotalLot} (${entry.nombreLotHabitation} habitation)`
    : String(entry.nombreTotalLot);
};

/**
 * Résumé de la copropriété sélectionnée dans le registre national (RNIC) : l'essentiel
 * pour choisir (immatriculation, syndic, lots), les détails administratifs repliés.
 * L'action de rattachement est portée par le bouton d'étape de la page, pas par la fiche.
 * Dans un panneau à hauteur bornée, seule la liste de détails défile : l'en-tête reste visible.
 */
export const CopropertyRegistryCard: FC<CopropertyRegistryCardProps> = ({ entry, onClear }) => {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div className="flex min-h-0 flex-col overflow-hidden rounded-2xl border border-primary/30 bg-primary/5">
      <div className="flex shrink-0 items-center gap-3 px-4 pt-3 pb-2">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Building2 className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-gray-900">
            {entry.nomCopropriete || entry.adresse || "Copropriété du registre"}
          </p>
          <p className="truncate text-xs text-gray-500">{entry.adresse}</p>
        </div>
        {onClear && (
          <button
            type="button"
            onClick={onClear}
            aria-label="Désélectionner"
            className="shrink-0 rounded-lg p-1 text-gray-400 hover:bg-white hover:text-gray-700"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="min-h-0 overflow-y-auto px-4 pb-3">
        <dl className="divide-y divide-primary/10">
          <Info label="N° d'immatriculation" value={entry.numeroImmatriculation} />
          <Info label="Syndic" value={entry.representantLegal} />
          <Info label="Lots" value={formatLots(entry)} />
          {showDetails && (
            <>
              <Info label="Type de syndic" value={entry.typeSyndic} />
              <Info label="SIRET du syndic" value={entry.numeroSiret} />
              <Info label="Mandat" value={entry.mandatEnCours} />
              <Info label="Période de construction" value={entry.periodeConstruction} />
              <Info label="Lots de stationnement" value={entry.nombreLotStationnement} />
            </>
          )}
        </dl>
        <button
          type="button"
          onClick={() => setShowDetails((open) => !open)}
          className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
        >
          {showDetails ? (
            <>
              Moins de détails <ChevronUp className="h-3 w-3" />
            </>
          ) : (
            <>
              Plus de détails <ChevronDown className="h-3 w-3" />
            </>
          )}
        </button>
      </div>
    </div>
  );
};
